import React, { useCallback } from 'react'

import {
  CitizenAIViewMode,
  useCitizenAIAssistant,
} from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import { Button, HStack, Icon, T, Tooltip, VStack } from 'src/govwell-ui'

type Props = {
  onClose: () => void
  viewMode: CitizenAIViewMode
  onMaximize?: () => void
  onMinimize?: () => void
  showSidebar?: boolean
}

const CitizenAIChatHeader = ({
  onClose,
  viewMode,
  onMaximize,
  onMinimize,
  showSidebar = true,
}: Props) => {
  const context = useCitizenAIAssistant()
  const isGenericMode = context?.isGenericMode ?? true
  const orgDisplayName = context?.currentOrgContext.orgDisplayName
  const customIconUrl = context?.currentOrgContext.aiChatIconUrl ?? undefined

  const handleToggleSize = useCallback(() => {
    if (viewMode === 'side') {
      onMaximize?.()
    } else {
      onMinimize?.()
    }
  }, [viewMode, onMaximize, onMinimize])

  const toggleIcon =
    viewMode === 'side' ? 'FaUpRightAndDownLeftFromCenter' : 'FaDownLeftAndUpRightToCenter'
  const toggleTooltip = viewMode === 'side' ? 'View as full page' : 'Minimize to side view'

  const title =
    isGenericMode || !orgDisplayName ? 'GovWell Assistant' : `${orgDisplayName} Assistant`
  const subtitle = 'Powered by GovWell'

  return (
    <div className="shrink-0 border-b border-neutral-200 bg-white px-4 py-3">
      <HStack gap={3}>
        {customIconUrl ? (
          <img src={customIconUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-900">
            <Icon.FaSparkles className="h-4 w-4 text-white" />
          </div>
        )}
        <VStack gap={0} className="min-w-0 flex-1">
          <T.Body
            className={`truncate font-semibold text-neutral-900 ${showSidebar ? 'max-w-md' : 'max-w-[180px]'}`}
          >
            {title}
          </T.Body>
          <T.BodySmall className="truncate text-neutral-500">{subtitle}</T.BodySmall>
        </VStack>
        <Tooltip content={toggleTooltip}>
          <span className="shrink-0">
            <Button
              variant="default"
              size="sm"
              icon={toggleIcon}
              onClick={handleToggleSize}
              ariaLabel={toggleTooltip}
            />
          </span>
        </Tooltip>
        <Button
          variant="default"
          size="sm"
          icon="FaXmark"
          onClick={onClose}
          ariaLabel="Close"
          className="shrink-0"
        />
      </HStack>
    </div>
  )
}

export default React.memo(CitizenAIChatHeader)
